import { useState } from "react";
import { useCountdownAction } from "@/hooks/useCountdownAction";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Trash2, X } from "lucide-react";

const SECTIONS = [
  { key: "orders", label: "Orders" },
  { key: "production", label: "Production (gate passes, machines, downtime)" },
  { key: "inventory", label: "Inventory & BOM" },
  { key: "sales", label: "Sales & Customers" },
  { key: "purchasing", label: "Purchasing (POs, suppliers, OSP vendors)" },
];

function ScopePicker({ wipeAll, selected, onWipeAll, onToggle }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm">What to Clear</Label>
      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="scope-all">
        <Checkbox checked={wipeAll} onCheckedChange={(v) => onWipeAll(!!v)} data-testid="scope-all-checkbox" />
        <span className="font-medium text-slate-800">Everything (all operational data)</span>
      </label>
      <div className={`pl-6 space-y-1.5 ${wipeAll ? "opacity-40 pointer-events-none" : ""}`}>
        {SECTIONS.map((s) => (
          <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`scope-${s.key}`}>
            <Checkbox checked={selected.includes(s.key)} onCheckedChange={() => onToggle(s.key)} disabled={wipeAll} data-testid={`scope-${s.key}-checkbox`} />
            <span className="text-slate-600">{s.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// Clear-data flow: pick scope -> type CLEAR -> 30 s cancellable countdown -> wipe.
export default function ClearDataDialog({ open, onClose, onClear }) {
  const [wipeAll, setWipeAll] = useState(true);
  const [selected, setSelected] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const reset = () => { setWipeAll(true); setSelected([]); setText(""); setBusy(false); };
  const fire = async () => {
    setBusy(true);
    try { await onClear(wipeAll ? [] : selected); reset(); onClose(); }
    catch { reset(); }
  };
  const { countdown, start, cancel } = useCountdownAction(fire);
  const close = () => { cancel(); reset(); onClose(); };
  const toggle = (key) => setSelected((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));
  const canStart = text.trim().toUpperCase() === "CLEAR" && (wipeAll || selected.length > 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="clear-data-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-red-700"><AlertTriangle className="h-5 w-5" /> Clear Data</DialogTitle>
          <DialogDescription>Permanently deletes the selected records. User accounts are always kept. This cannot be undone.</DialogDescription>
        </DialogHeader>
        {countdown === null ? (
          <div className="space-y-4">
            <ScopePicker wipeAll={wipeAll} selected={selected} onWipeAll={(v) => { setWipeAll(v); if (v) setSelected([]); }} onToggle={toggle} />
            <div className="space-y-2">
              <Label className="text-sm">Type <span className="font-mono-data font-semibold text-red-600">CLEAR</span> to confirm</Label>
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="CLEAR" autoFocus data-testid="clear-data-confirm-input" />
            </div>
          </div>
        ) : (
          <div className="text-center py-4" data-testid="clear-data-countdown">
            <div className="text-5xl font-bold font-mono-data text-red-600 tabular-nums">{countdown}</div>
            <p className="text-sm text-slate-500 mt-2">{busy ? "Clearing data…" : "Reset starts in a moment. Press Cancel to stop."}</p>
          </div>
        )}
        <DialogFooter>
          {countdown === null ? (
            <>
              <Button variant="outline" onClick={close} data-testid="clear-data-cancel">Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" disabled={!canStart} onClick={start} data-testid="clear-data-start-btn">
                <Trash2 className="h-4 w-4 mr-1.5" /> {wipeAll ? "Wipe Everything" : "Wipe Selected"}
              </Button>
            </>
          ) : (
            <Button variant="outline" className="w-full border-slate-300" disabled={busy} onClick={cancel} data-testid="clear-data-cancel-countdown">
              <X className="h-4 w-4 mr-1.5" /> Cancel Reset
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
